// frontend/src/services/offlineService.js
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { Preferences } from "@capacitor/preferences";
import axios from "axios";
import { del, get, keys, set } from "idb-keyval";
import Vue from "vue";

// ─────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────
const QUEUE_KEY = "offline_queue"; // clave en Preferences (nativo)
const CACHE_PREFIX = "cache_"; // prefijo en IndexedDB
const CACHE_TTL_MS = 1000 * 60 * 60; // 1 hora de tiempo de vida del caché

// ─────────────────────────────────────────────
// ESTADO REACTIVO DE RED
// ─────────────────────────────────────────────
export const networkState = Vue.observable({ isOnline: true });

// ─────────────────────────────────────────────
// INICIALIZACIÓN: escuchar cambios de red
// ─────────────────────────────────────────────

/**
 * Inicializa el listener de red. Solo se activa en plataformas nativas (Android/iOS).
 */
export async function initNetworkListener() {
  // Solo activar en móvil nativo
  if (!Capacitor.isNativePlatform()) return;

  // Estado inicial
  const status = await Network.getStatus();
  networkState.isOnline = status.connected;

  // Suscribirse a cambios
  Network.addListener("networkStatusChange", async (status) => {
    const wasOffline = !networkState.isOnline;
    networkState.isOnline = status.connected;

    if (status.connected && wasOffline) {
      // Acaba de recuperar conexión → sincronizar cola
      console.log("[Offline] Conexión recuperada. Sincronizando...");
      await syncQueue();
    }
  });
}

// ─────────────────────────────────────────────
// CACHÉ DE DATOS (GET)
// ─────────────────────────────────────────────

/**
 * Guarda la respuesta de una petición GET en IndexedDB.
 * @param {string} url - URL de la petición
 * @param {any} data   - Datos a cachear
 */
export async function cacheSet(url, data) {
  await set(`${CACHE_PREFIX}${url}`, { data, timestamp: Date.now() });
}

/**
 * Recupera datos cacheados si existen y no han expirado.
 * @param {string} url
 * @returns {any|null}
 */
export async function cacheGet(url) {
  const entry = await get(`${CACHE_PREFIX}${url}`);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    await del(`${CACHE_PREFIX}${url}`);
    return null;
  }
  return entry.data;
}

/**
 * Elimina la entrada de caché para una URL concreta.
 * @param {string} url
 */
export async function cacheDelete(url) {
  await del(`${CACHE_PREFIX}${url}`);
}

/**
 * Invalida toda la caché GET para forzar recarga fresca.
 */
export async function invalidateCache() {
  const allKeys = await keys();
  const cacheKeys = allKeys.filter((k) => String(k).startsWith(CACHE_PREFIX));
  await Promise.all(cacheKeys.map((k) => del(k)));
  console.log(
    `[Offline] Caché invalidada (${cacheKeys.length} entradas eliminadas)`,
  );
}

// ─────────────────────────────────────────────
// COLA DE OPERACIONES PENDIENTES (POST/PUT/DELETE)
// ─────────────────────────────────────────────

/**
 * Obtiene la cola actual de operaciones pendientes.
 * @returns {Array}
 */
async function getQueue() {
  const { value } = await Preferences.get({ key: QUEUE_KEY });
  return value ? JSON.parse(value) : [];
}

/**
 * Persiste la cola.
 * @param {Array} queue
 */
async function saveQueue(queue) {
  await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(queue) });
}

/**
 * Añade una operación a la cola de sincronización offline.
 * @param {object} operation - { method, url, data, headers }
 */
export async function enqueueOperation(operation) {
  const queue = await getQueue();
  const token = localStorage.getItem("token");
  queue.push({
    id: `${Date.now()}_${Math.random()}`,
    timestamp: new Date().toISOString(),
    _savedToken: token, // guardar token en el momento de encolar
    ...operation,
  });
  await saveQueue(queue);
  console.log(
    `[Offline] Operación encolada: ${operation.method} ${operation.url}`,
  );
  // Notificar a los componentes que la cola ha cambiado
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  }
}

/**
 * Sincroniza todas las operaciones pendientes con el servidor.
 * Ejecuta las operaciones en orden y elimina las que tienen éxito.
 */
export async function syncQueue() {
  const queue = await getQueue();
  if (queue.length === 0) {
    console.log("[Offline] Cola vacía. Nada que sincronizar.");
    return { synced: 0, failed: 0 };
  }

  console.log(
    `[Offline] Sincronizando ${queue.length} operaciones pendientes...`,
  );
  const remaining = [];
  let synced = 0;
  let failed = 0;

  for (const op of queue) {
    try {
      // Usar el token guardado al encolar o el token actual
      const token = op._savedToken || localStorage.getItem("token");
      await axios({
        method: op.method,
        url: op.url,
        data: op.data,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...op.headers,
        },
      });
      synced++;
      console.log(`[Offline] ✓ Sincronizado: ${op.method} ${op.url}`);
    } catch (err) {
      failed++;
      console.error(
        `[Offline] ✗ Error al sincronizar: ${op.method} ${op.url}`,
        err,
      );
      remaining.push(op); // mantener en cola para reintentar
    }
  }

  await saveQueue(remaining);

  // Notificar a los componentes que la cola ha cambiado
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  }

  // Invalidar caché si hubo éxitos para forzar datos frescos
  if (synced > 0) {
    await invalidateCache();
  }

  console.log(
    `[Offline] Sincronización completa. Éxitos: ${synced}, Fallos: ${failed}`,
  );
  return { synced, failed };
}

/**
 * Devuelve el número de operaciones pendientes en la cola.
 */
export async function getPendingCount() {
  const queue = await getQueue();
  return queue.length;
}
